import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithRedirect,
  getRedirectResult,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc, 
  serverTimestamp, 
  arrayUnion 
} from "firebase/firestore";
import firebaseConfig from "./firebaseConfig";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";

// Initialize Firebase app safely
let app;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

// Initialize services
export const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// Configure local auth persistence
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error("Firebase auth: Error setting persistence", error);
});

/**
 * Creates a clean default profile template for new user documents
 */
export const createNewUserProfileObject = (user) => {
  const namePart = user.displayName || (user.email ? user.email.split('@')[0] : 'user');
  const handle = namePart.toLowerCase().replace(/[^a-z0-9_]/g, '').substring(0, 20);

  const isGoogleAuth = user.providerData?.some(p => p.providerId === 'google.com') || false;
  const authMethod = isGoogleAuth ? 'google' : 'email_password';

  const cleanEmail = user.email ? user.email.toLowerCase().trim() : null;

  return {
    uid: user.uid,
    email: cleanEmail,
    // Store array of associated emails for 1-to-Many email/artist mapping
    associatedEmails: cleanEmail ? [cleanEmail] : [],
    
    displayName: user.displayName || 'Anonymous',
    displayName_lowercase: (user.displayName || 'Anonymous').toLowerCase(),
    handle: handle,
    handle_lowercase: handle.toLowerCase(),
    photoURL: user.photoURL || null,
    createdAt: serverTimestamp(),
    
    // Localization
    suburb: null,
    state: null,
    country: "Australia",
    isGhosted: false,

    // Onboarding lifecycle
    isOnboarded: false,
    onboardingStep: 1,
    accountRole: "listener",

    // Compliance
    termsAccepted: false,
    termsAcceptedAt: null,
    legalAgreementVersion: "3.0-JULY-2026",

    // Engagement Metrics
    points: 0,
    lifetimePoints: 0,

    // Linked Artist Accounts
    linkedArtistAccounts: [],

    // Subscription status
    premiumSubscription: {
      status: "None",
      priceId: null,
      subscriptionId: null,
      currentPeriodEnd: null,
    },

    authMethod: authMethod
  };
};

/**
 * Appends an email address to a user's associatedEmails array without duplicates
 */
export const addAssociatedEmailToUser = async (userId, newEmail) => {
  if (!userId || !newEmail) return;
  const cleanEmail = newEmail.toLowerCase().trim();
  const userRef = doc(db, "users", userId);

  await setDoc(
    userRef,
    {
      associatedEmails: arrayUnion(cleanEmail)
    },
    { merge: true }
  );
};

// Sign Up
export const signUp = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    const newUserProfile = createNewUserProfileObject(user);
    await createOrUpdateUserProfile(user.uid, newUserProfile);

    const functions = getFunctions();
    const incrementUserCount = httpsCallable(functions, 'incrementUserCount');
    await incrementUserCount();

    return user;
  } catch (error) {
    console.error("❌ Error signing up:", error);
    throw error;
  }
};

// Log In
export const logIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error("Error logging in:", error);
    throw error;
  }
};

// Google Sign-In Redirect
export const googleSignIn = () => {
  const provider = new GoogleAuthProvider();
  return signInWithRedirect(auth, provider);
};

// Handle Google Redirect Result
export const handleGoogleRedirectResult = async () => {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      const user = result.user;
      const functions = getFunctions();

      const existingProfile = await fetchUserProfile(user.uid);

      if (!existingProfile) {
        // New User via Google
        const newUserProfile = createNewUserProfileObject(user);

        const incrementUserCount = httpsCallable(functions, 'incrementUserCount');
        await incrementUserCount();

        await createOrUpdateUserProfile(user.uid, newUserProfile);
      } else {
        // Returning User via Google: Append email to associatedEmails if new
        const cleanEmail = user.email ? user.email.toLowerCase().trim() : null;

        const updateData = {
          lastLogin: new Date().toISOString(),
          email: cleanEmail
        };

        if (cleanEmail) {
          updateData.associatedEmails = arrayUnion(cleanEmail);
        }

        if (!existingProfile.photoURL && user.photoURL) {
          updateData.photoURL = user.photoURL;
        }

        await createOrUpdateUserProfile(user.uid, updateData);
      }

      const finalProfile = await fetchUserProfile(user.uid);
      return { user, profile: finalProfile };
    }
    return null;
  } catch (error) {
    console.error("Error handling Google redirect result:", error);
    throw error;
  }
};

// Log Out
export const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error logging out:", error);
    throw error;
  }
};

// Fetch User Profile
export const fetchUserProfile = async (userId) => {
  try {
    if (!userId) {
      console.warn("fetchUserProfile called without a userId.");
      return null;
    }
    const docRef = doc(db, "users", userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

// Create or Update User Profile
export const createOrUpdateUserProfile = async (userId, profileData) => {
  try {
    if (!userId) {
      console.error("createOrUpdateUserProfile called without a userId.");
      return;
    }

    const docRef = doc(db, "users", userId);
    await setDoc(docRef, profileData, { merge: true });
  } catch (error) {
    console.error("❌ Error creating/updating user profile:", error);
    throw error;
  }
};

// Password Reset
export const resetPassword = async (email) => {
  const activeOrigin = window.location.origin;

  const actionCodeSettings = {
    url: `${activeOrigin}/login`,
    handleCodeInApp: true,
  };

  try {
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
};

// Auth State Listener
export const onAuthStateChangedListener = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Media & Storage Utilities
export const uploadProfilePicture = async (userId, file) => {
  try {
    const storageRef = ref(storage, `profilePictures/${userId}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("Error uploading profile picture:", error);
    throw error;
  }
};

export const uploadMusicFile = async (userId, file, metadata) => {
  const storageRef = ref(storage, `music_uploads/${userId}/${file.name}`);
  await uploadBytes(storageRef, file);
  const fileURL = await getDownloadURL(storageRef);

  const musicCollection = collection(db, "music");
  const musicDoc = await addDoc(musicCollection, {
    ...metadata,
    fileURL,
    userId,
    uploadTimestamp: new Date().toISOString(),
  });

  return musicDoc.id;
};